import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/cn';
import { HelpIcon } from './HelpIcon';
import { FieldHelpInline } from './FieldHelpInline';
import { FieldAiActions } from '@/skills/ai-text-rewrite/FieldAiActions';
import { DiktatKnopf } from '@/skills/voice-control';

/**
 * Setzt einen Wert so in ein Eingabefeld, dass React-Hook-Form, Controller und
 * einfache `onChange`-Handler ihn gleichermaßen mitbekommen.
 *
 * Der native Value-Setter ist nötig, weil React den Property-Setter
 * überschreibt — ein direktes `node.value = x` löst kein `input`-Event aus, und
 * der Formular-State bliebe auf dem alten Stand stehen.
 */
function setNativeValue(node: HTMLInputElement | HTMLTextAreaElement, next: string): void {
  const prototype =
    node instanceof HTMLTextAreaElement
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  if (setter) {
    setter.call(node, next);
  } else {
    node.value = next;
  }
  node.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Spiegelt den DOM-Wert eines Feldes in React-State — nur nötig, wenn das Feld
 * KI-Aktionen anbietet (die brauchen den aktuellen Text synchron).
 *
 * Warum aus dem DOM statt aus den Props: dasselbe Feld wird mal controlled
 * (`value`), mal uncontrolled über `register()` benutzt, und `form.reset()`
 * schreibt direkt in den Knoten. Nur der Knoten kennt in allen drei Fällen den
 * aktuellen Stand.
 */
function useValueMirror(
  ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
  enabled: boolean,
): [string, (next: string) => void] {
  const [currentText, setCurrentText] = useState('');
  // Bewusst OHNE Abhängigkeitsliste: der Knoten kann sich auch ändern, ohne dass
  // sich eine Abhängigkeit ändert (form.reset, setValue schreiben direkt in den
  // Knoten). Der Gleichheits-Check darunter beendet die Schleife nach einem
  // Durchlauf — ohne ihn wäre das eine Endlosschleife.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (el && el.value !== currentText) setCurrentText(el.value);
  });
  return [currentText, setCurrentText];
}

/** Gemeinsame Doku beider `aiContextHint`-Props (siehe Prop-Kommentare). */
interface AiCapableField {
  /**
   * Wenn gesetzt, bekommt das Feld die Textwerkzeuge als schmale Leiste
   * darunter: KI-Aktionen (Aus Stichworten erzeugen, Umformulieren, Kürzen,
   * Erweitern, Fachsprache, Feld leeren) samt Rückgängig — und daneben das
   * Diktat. Beides gehört zusammen: „Text erzeugen oder ändern".
   *
   * Nur für FREITEXT setzen. Bei Auswahl-, Zahlen- und Datumsfeldern ist
   * Umformulieren sinnlos und Raten fachlich gefährlich; diktieren wäre dort
   * ebenso wenig sinnvoll.
   *
   * DATENMINIMIERUNG: der Hinweis geht 1:1 als `contextHint` ans Backend und
   * damit an den KI-Anbieter — keine Kundennamen, keine Anschriften, kein
   * Projekttitel, nur eine feste Beschreibung des Feldes.
   */
  aiContextHint?: string;
}

/**
 * Werkzeugleiste unter einem Freitextfeld: KI-Aktionen und Diktat
 * nebeneinander, nie übereinander.
 *
 * Beide Teile blenden sich selbst aus (KI: fehlende Berechtigung oder
 * gesperrtes Feld; Diktat: Feature-Flag aus oder Browser kann es nicht).
 * Fallen beide weg, ist der Container leer — `empty:hidden` nimmt ihm dann
 * auch den Abstand, damit das Feld exakt so aussieht wie vorher.
 */
function FieldTextTools({
  value,
  onApply,
  contextHint,
  fieldLabel,
  disabled,
  onUnreviewedChange,
}: {
  value: string;
  onApply: (next: string) => void;
  contextHint: string;
  fieldLabel?: string;
  disabled?: boolean;
  onUnreviewedChange: (unreviewed: boolean) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 empty:hidden">
      <FieldAiActions
        value={value}
        onApply={onApply}
        contextHint={contextHint}
        disabled={disabled}
        onUnreviewedChange={onUnreviewedChange}
      />
      {/* Höhe auf die KI-Schaltfläche gezogen: in dieser Leiste soll kein
          Bedienelement das andere optisch verdrängen. */}
      <DiktatKnopf
        wert={value}
        onWert={onApply}
        feldName={fieldLabel}
        deaktiviert={disabled}
        className="h-[22px] min-w-[22px] px-1.5 text-[11px]"
      />
    </div>
  );
}

interface Props extends InputHTMLAttributes<HTMLInputElement>, AiCapableField {
  label?: string;
  hint?: string;
  error?: string;
  trailing?: ReactNode;
  /** Optionaler Tooltip-Inventory-Key, rendert ein HelpIcon neben dem Label. */
  helpKey?: string;
}

export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  {
    label,
    hint,
    error,
    trailing,
    helpKey,
    id,
    className,
    disabled,
    aiContextHint,
    onChange,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const describedById = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  const aiEnabled = Boolean(aiContextHint);

  const innerRef = useRef<HTMLInputElement | null>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement, []);
  const [currentText, setCurrentText] = useValueMirror(innerRef, aiEnabled);
  const [aiUnreviewed, setAiUnreviewed] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (aiEnabled) setCurrentText(e.target.value);
    onChange?.(e);
  };

  const applyAiValue = useCallback(
    (next: string) => {
      const node = innerRef.current;
      if (!node) return;
      setNativeValue(node, next);
      setCurrentText(next);
    },
    [setCurrentText],
  );

  // Hover/Focus-Lift analog Button: nur wenn weder disabled noch in Fehler-State,
  // damit der Validierungs-Schwerpunkt (rote Border) nicht visuell konkurriert.
  const liftClass =
    !disabled && !error
      ? 'hover:-translate-y-px hover:shadow-elev-1 focus:-translate-y-px focus:shadow-elev-1'
      : '';

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label
          htmlFor={inputId}
          className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-ink-500"
        >
          <span>{label}</span>
          {helpKey ? <HelpIcon helpKey={helpKey} /> : null}
        </label>
      ) : null}
      <div className="relative">
        <input
          ref={innerRef}
          id={inputId}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedById}
          onChange={handleChange}
          className={cn(
            'h-10 w-full rounded border bg-paper px-3 text-sm text-ink-900 placeholder:text-ink-400',
            'transition-all duration-150',
            'focus:outline-none focus:ring-1 focus:ring-accent-500',
            liftClass,
            error
              ? 'border-danger focus:border-danger focus:ring-danger'
              : aiUnreviewed
                ? 'border-accent-400 bg-accent-50/40 focus:border-accent-500'
                : 'border-ink-300 focus:border-accent-500 dark:focus:border-accent-400',
            trailing && 'pr-10',
            'disabled:bg-ink-50 disabled:text-ink-500 disabled:cursor-not-allowed',
            className,
          )}
          {...rest}
        />
        {trailing ? (
          <span className="absolute inset-y-0 right-2 flex items-center text-ink-500">
            {trailing}
          </span>
        ) : null}
      </div>
      {aiContextHint ? (
        <FieldTextTools
          value={currentText}
          onApply={applyAiValue}
          contextHint={aiContextHint}
          fieldLabel={label}
          disabled={disabled}
          onUnreviewedChange={setAiUnreviewed}
        />
      ) : null}
      {error ? (
        <p id={`${inputId}-error`} className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-ink-500">
          {hint}
        </p>
      ) : null}
      {helpKey ? <FieldHelpInline helpKey={helpKey} /> : null}
    </div>
  );
});

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement>, AiCapableField {
  label?: string;
  hint?: string;
  error?: string;
  /** Optionaler Tooltip-Inventory-Key, rendert ein HelpIcon neben dem Label. */
  helpKey?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    helpKey,
    id,
    className,
    rows = 4,
    aiContextHint,
    disabled,
    onChange,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const describedById = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  const aiEnabled = Boolean(aiContextHint);

  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement, []);
  const [currentText, setCurrentText] = useValueMirror(innerRef, aiEnabled);
  const [aiUnreviewed, setAiUnreviewed] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (aiEnabled) setCurrentText(e.target.value);
    onChange?.(e);
  };

  const applyAiValue = useCallback(
    (next: string) => {
      const node = innerRef.current;
      if (!node) return;
      setNativeValue(node, next);
      setCurrentText(next);
    },
    [setCurrentText],
  );

  const liftClass =
    !disabled && !error
      ? 'hover:-translate-y-px hover:shadow-elev-1 focus:-translate-y-px focus:shadow-elev-1'
      : '';

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label
          htmlFor={inputId}
          className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-ink-500"
        >
          <span>{label}</span>
          {helpKey ? <HelpIcon helpKey={helpKey} /> : null}
        </label>
      ) : null}
      <textarea
        ref={innerRef}
        id={inputId}
        rows={rows}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedById}
        disabled={disabled}
        onChange={handleChange}
        className={cn(
          'w-full rounded border bg-paper px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400',
          'transition-all duration-150',
          'focus:outline-none focus:ring-1 focus:ring-accent-500',
          liftClass,
          error
            ? 'border-danger focus:border-danger focus:ring-danger'
            : aiUnreviewed
              ? 'border-accent-400 bg-accent-50/40 focus:border-accent-500'
              : 'border-ink-300 focus:border-accent-500 dark:focus:border-accent-400',
          'disabled:bg-ink-50 disabled:text-ink-500 disabled:cursor-not-allowed',
          className,
        )}
        {...rest}
      />
      {aiContextHint ? (
        <FieldTextTools
          value={currentText}
          onApply={applyAiValue}
          contextHint={aiContextHint}
          fieldLabel={label}
          disabled={disabled}
          onUnreviewedChange={setAiUnreviewed}
        />
      ) : null}
      {error ? (
        <p id={`${inputId}-error`} className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-ink-500">
          {hint}
        </p>
      ) : null}
      {helpKey ? <FieldHelpInline helpKey={helpKey} /> : null}
    </div>
  );
});
